import { CustomColumnHeaderToClone, Lot, LotChanges, QualQuestion } from 'types';
import { processImportedLot } from './processImportedLot';

interface ProcessAllImportedLotsParams {
  allSurveyQuestions: QualQuestion[];
  changes: Record<string, LotChanges>;
  deletedQualifyingCustomColumnHeaders?: CustomColumnHeaderToClone[];
  errorLotIndexes: number[];
  importedLots: Lot[];
  maxSno: number;
  replaceAll?: boolean;
}

interface ProcessAllImportedLotsResult {
  allNewLotQuestions: QualQuestion[];
  newLots: Lot[];
  updatedChanges: Record<string, LotChanges>;
}

/**
 * Processes all imported lots, filtering out lots with errors and adding them to the changes object.
 * Creates a new updated changes object rather than modifying the input.
 *
 * @param props
 * @param props.allSurveyQuestions {QualQuestion[]} - All survey questions available
 * @param props.changes {Record<string, LotChanges>} - The current changes record
 * @param props.deletedQualifyingCustomColumnHeaders {CustomColumnHeaderToClone[]} - Custom Column Headers that were deleted from replaceAll
 * @param props.errorLotIndexes {number[]} - Array of indexes of lots with errors that should be skipped
 * @param props.importedLots {Lot[]} - Array of lots to be imported
 * @param props.maxSno {number} - The maximum serial number from existing lots
 * @param props.replaceAll {boolean} - The flag indicating if this is a replace all operation
 * @returns {ProcessAllImportedLotsResult} Object containing the results of processing all lots
 * @returns {QualQuestion[]} Object.allNewLotQuestions - All questions generated for the imported lots
 * @returns {Lot[]} Object.newLots - Array of processed lots with defaults added
 * @returns {Record<string, LotChanges>} Object.updatedChanges - A new changes object with all updates
 */
export const processAllImportedLots = ({
  allSurveyQuestions,
  changes,
  deletedQualifyingCustomColumnHeaders = [],
  errorLotIndexes,
  importedLots,
  maxSno,
  replaceAll,
}: ProcessAllImportedLotsParams): ProcessAllImportedLotsResult => {
  const newLots: Lot[] = [];
  const allNewLotQuestions: QualQuestion[] = [];
  let updatedChanges = { ...changes };

  // Process each imported lot that doesn't have errors
  importedLots.forEach((newIncomingLot, index) => {
    // Skip lots with errors
    if (errorLotIndexes.includes(index)) {
      return;
    }

    const newSno = maxSno + index + 1;

    const processParams = {
      allSurveyQuestions,
      changes: updatedChanges,
      newIncomingLot,
      newSno,
      replaceAll,
      // Conditionally include deleted items only when replaceAll is true
      ...(replaceAll ? {
        deletedQualifyingCustomColumnHeaders,
      } : {})
    };

    const { lot, questions, updatedChanges: newChanges } = processImportedLot(processParams);

    updatedChanges = newChanges;
    newLots.push(lot);
    allNewLotQuestions.push(...questions);
  });

  return { allNewLotQuestions, newLots, updatedChanges };
};
